import React, { useState, useEffect, useRef } from 'react';
import { 
  StyleSheet, 
  View, 
  ScrollView, 
  KeyboardAvoidingView, 
  Platform,
  TextInput as RNTextInput,
  TouchableOpacity,
  Animated,
  ImageBackground,
  FlatList
} from 'react-native';
import { Text, useTheme, TextInput, Button, Avatar, FAB, Portal, Menu, Chip, ActivityIndicator } from 'react-native-paper';
import { LinearGradient } from 'expo-linear-gradient';
import { theme } from '../theme/theme';
import { format } from 'date-fns';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AstrologyService } from '../services/AstrologyService';
import { Ionicons } from '@expo/vector-icons';
import Markdown from 'react-native-markdown-display';
import { AuthenticationService } from '../services/AuthenticationService';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, updateDoc, setDoc, getDocs, collection, query, orderBy, where, limit, startAfter, QueryDocumentSnapshot, DocumentData, getDoc, onSnapshot, DocumentSnapshot } from 'firebase/firestore';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
}

interface UserProfile {
  name: string;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  zodiacSign: string;
  isProfileComplete: boolean;
}

interface PromptChip {
  id: string;
  text: string;
  icon: string;
  getPrompt: (profile: UserProfile) => string;
}

const PROMPT_CHIPS: PromptChip[] = [
  { 
    id: '1', 
    text: "I want to share what's on my mind", 
    icon: 'brain',
    getPrompt: (profile: UserProfile) => `I want to share what's on my mind. I was born on ${profile.birthDate} at ${profile.birthTime} in ${profile.birthPlace}.`
  },
  { 
    id: '2', 
    text: "I want to talk about how I'm feeling", 
    icon: 'heart',
    getPrompt: (profile: UserProfile) => `I want to talk about how I'm feeling. I was born on ${profile.birthDate} at ${profile.birthTime} in ${profile.birthPlace}.`
  },
  { 
    id: '3', 
    text: "I'm facing some challenges", 
    icon: 'lightbulb',
    getPrompt: (profile: UserProfile) => `I'm facing some challenges. I was born on ${profile.birthDate} at ${profile.birthTime} in ${profile.birthPlace}.`
  },
  { 
    id: '4', 
    text: "I'm looking for guidance", 
    icon: 'star',
    getPrompt: (profile: UserProfile) => `I'm looking for guidance. I was born on ${profile.birthDate} at ${profile.birthTime} in ${profile.birthPlace}.`
  },
];

const db = getFirestore();

// Define getFieldPrompt before use
function getFieldPrompt(field: string) {
  switch (field) {
    case 'birthDate':
      return 'When is your birth date? (MM/DD/YYYY)';
    case 'birthTime':
      return 'What time were you born? (HH:MM, 24-hour format)';
    case 'birthPlace':
      return 'Where were you born? (City, Country)';
    default:
      return '';
  }
}

export default function ChatScreen({ navigation }: any) {
  const { colors } = useTheme();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [showPromptChips, setShowPromptChips] = useState(false);
  const scrollViewRef = useRef<FlatList<Message>>(null);
  const inputRef = useRef<any>(null);
  const [showFAB, setShowFAB] = useState(true);
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const [chatHistory, setChatHistory] = useState<{ role: 'user' | 'assistant', content: string }[]>([]);
  const [menuVisible, setMenuVisible] = useState(false);
  const REQUIRED_FIELDS = ['name', 'birthDate', 'birthTime', 'birthPlace'];
  const [chatLoading, setChatLoading] = useState(false);
  const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Add animation values for typing dots
  const dot1Anim = useRef(new Animated.Value(0)).current;
  const dot2Anim = useRef(new Animated.Value(0)).current;
  const dot3Anim = useRef(new Animated.Value(0)).current;

  // Function to start typing animation
  const startTypingAnimation = () => {
    const createAnimation = (value: Animated.Value, delay: number) => {
      return Animated.sequence([
        Animated.delay(delay),
        Animated.loop(
          Animated.sequence([
            Animated.timing(value, {
              toValue: 1,
              duration: 400,
              useNativeDriver: true,
            }),
            Animated.timing(value, {
              toValue: 0,
              duration: 400,
              useNativeDriver: true,
            }),
          ])
        ),
      ]);
    };

    Animated.parallel([
      createAnimation(dot1Anim, 0),
      createAnimation(dot2Anim, 200),
      createAnimation(dot3Anim, 400),
    ]).start();
  };

  // Function to stop typing animation
  const stopTypingAnimation = () => {
    dot1Anim.stopAnimation();
    dot2Anim.stopAnimation();
    dot3Anim.stopAnimation();
    dot1Anim.setValue(0);
    dot2Anim.setValue(0);
    dot3Anim.setValue(0);
  };

  useEffect(() => {
    async function init() {
      // Load user profile from AsyncStorage or Firestore
      let profile = null;
      const localProfile = await AsyncStorage.getItem('userProfile');
      if (localProfile) {
        profile = JSON.parse(localProfile);
      } else {
        // Try Firestore
        const auth = getAuth();
        const user = auth.currentUser;
        if (user) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            profile = userDoc.data();
            await AsyncStorage.setItem('userProfile', JSON.stringify(profile));
          }
        }
      }
      if (profile) {
        setUserProfile(profile);
        // Check for missing fields
        const REQUIRED_FIELDS = ['name', 'birthDate', 'birthTime', 'birthPlace'];
        const firstMissing = REQUIRED_FIELDS.find(field => !profile[field]);
        if (firstMissing) {
          // Prompt for the next missing field
          addMessage({
            id: Date.now().toString(),
            text: getFieldPrompt(firstMissing),
            sender: 'assistant',
            timestamp: new Date(),
          });
          setShowPromptChips(false);
          return; // Do NOT load chat history
        } else {
          // Profile is complete, load chat history
          const auth = getAuth();
          const user = auth.currentUser;
          if (user) {
            await loadChatHistory(user.uid);
          }
        }
      } else {
        // No profile at all, start onboarding
        addMessage({
          id: Date.now().toString(),
          text: "Hello! I'm your personal astrology assistant. To get started, I'll need some information about you. What's your name?",
          sender: 'assistant',
          timestamp: new Date(),
        });
        setShowPromptChips(false);
      }
    }
    init();

    // Add Firestore listener for profile changes
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      const unsubscribe = onSnapshot(doc(db, 'users', user.uid), (doc: DocumentSnapshot<DocumentData>) => {
        if (doc.exists()) {
          const newProfile = doc.data() as UserProfile;
          setUserProfile(newProfile);
          AsyncStorage.setItem('userProfile', JSON.stringify(newProfile));
        }
      });

      // Cleanup listener on unmount
      return () => unsubscribe();
    }
  }, []);

  // Add new useEffect for focusing input
  useEffect(() => {
    // Focus the input after a short delay to ensure the component is fully mounted
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const loadChatHistory = async (userId: string) => {
    setChatLoading(true);
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Firestore] Starting to load chat history for userId:', userId);
      }
      
      // First verify the user is authenticated
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        console.error('[Firestore] No authenticated user found');
        return;
      }

      // Create the query with proper security rules in mind
      const q = query(
        collection(db, 'chats'),
        where('userId', '==', user.uid), // Use the current user's ID
        orderBy('timestamp', 'desc'),
        limit(10)
      );

      if (process.env.NODE_ENV === 'development') {
        console.log('[Firestore] Executing query with userId:', user.uid);
      }

      const querySnapshot = await getDocs(q);
      const loadedMessages: Message[] = querySnapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: data.id,
          text: data.text,
          sender: data.sender,
          timestamp: new Date(data.timestamp),
        };
      });
      
      // Welcome message logic: only add if the first message is not the welcome message
      if (userProfile) {
        const welcomeMessage = `Welcome back, ${userProfile.name}! How can I help you with your astrological journey today?`;
        const firstMessage = loadedMessages[0];
        
        // Only add welcome message if there are no messages or if the first message is not the welcome message
        if (!firstMessage || (firstMessage.text !== welcomeMessage || firstMessage.sender !== 'assistant')) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[Firestore] Adding welcome message as it is not the most recent message');
          }
          setMessages([
            {
              id: Date.now().toString(),
              text: welcomeMessage,
              sender: 'assistant',
              timestamp: new Date(),
            },
            ...loadedMessages,
          ]);
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.log('[Firestore] Welcome message already exists as most recent message');
          }
          setMessages(loadedMessages);
        }
        setShowPromptChips(true);
      } else {
        setMessages(loadedMessages);
      }
      
      setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1] || null);
      
      if (process.env.NODE_ENV === 'development') {
        if (loadedMessages.length === 0) {
          console.log('[Firestore] No messages found for userId:', user.uid);
        } else {
          console.log(`[Firestore] Loaded ${loadedMessages.length} messages for userId:`, user.uid);
          console.log('[Firestore] Message IDs:', loadedMessages.map(m => m.id));
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[Firestore] Error loading chat history:', error);
        // Log more details about the error
        if (error instanceof Error) {
          console.error('[Firestore] Error details:', {
            name: error.name,
            message: error.message,
            stack: error.stack
          });
        }
      }
    } finally {
      setChatLoading(false);
    }
  };

  const loadMoreMessages = async (userId: string) => {
    if (!lastVisible) return;
    setLoadingMore(true);
    try {
      const q = query(
        collection(db, 'chats'),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        startAfter(lastVisible),
        limit(10)
      );
      const querySnapshot = await getDocs(q);
      const moreMessages: Message[] = querySnapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: data.id,
          text: data.text,
          sender: data.sender,
          timestamp: new Date(data.timestamp),
        };
      });
      setMessages(prev => [...prev, ...moreMessages]);
      setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1] || lastVisible);
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Firestore] Loaded ${moreMessages.length} more messages for userId:`, userId);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[Firestore] Error loading more messages:', error);
      }
    } finally {
      setLoadingMore(false);
    }
  };

  const addMessage = async (message: Message) => {
    // Dedupe: compare with last message
    const lastMessage = messages[0]; // Check first message since list is inverted
    if (
      lastMessage &&
      lastMessage.text === message.text &&
      lastMessage.sender === message.sender
    ) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Chat] Duplicate message detected, not storing:', message);
      }
      return;
    }

    setMessages(prev => [message, ...prev]);

    // Save message to flat 'chats' collection with userId
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        console.error('[Chat] No authenticated user found when trying to save message');
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('[Chat] Current user:', {
          uid: user.uid,
          email: user.email,
          isAnonymous: user.isAnonymous
        });
        console.log('[Chat] Attempting to save message:', {
          messageId: message.id,
          text: message.text,
          sender: message.sender,
          timestamp: message.timestamp,
          userId: user.uid
        });
      }

      const messageData = {
        ...message,
        userId: user.uid,
        timestamp: message.timestamp.toISOString(),
      };

      await setDoc(
        doc(db, 'chats', message.id),
        messageData
      );

      if (process.env.NODE_ENV === 'development') {
        console.log('[Chat] Successfully saved message to Firestore');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[Chat] Error saving message to Firestore:', error);
        if (error instanceof Error) {
          console.error('[Chat] Error details:', {
            name: error.name,
            message: error.message,
            stack: error.stack
          });
        }
      }
    }
  };

  // Helper to update user profile
  const updateUserProfile = (key: keyof UserProfile, value: string) => {
    setUserProfile(prev => {
      const updated = { ...prev, [key]: value } as UserProfile;
      AsyncStorage.setItem('userProfile', JSON.stringify(updated));
      return updated;
    });
  };

  // Validation helpers
  function isValidDate(dateStr: string) {
    // MM/DD/YYYY
    const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    if (!regex.test(dateStr)) return false;
    const [month, day, year] = dateStr.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    return date.getMonth() + 1 === month && date.getDate() === day && date.getFullYear() === year;
  }
  function isValidTime(timeStr: string) {
    // HH:MM 24-hour
    const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    return regex.test(timeStr);
  }
  function isValidLocation(loc: string) {
    // At least two words (city, country)
    return loc.trim().split(/\s+/).length >= 2 && loc.includes(',');
  }

  const handlePromptChipPress = (chip: PromptChip) => {
    setShowPromptChips(false);
    
    if (!userProfile) {
      console.error('[Chat] No user profile found when handling prompt chip press');
      return;
    }
    
    // Add the user's selected prompt as a message with their astrological information
    const userMessage: Message = {
      id: Date.now().toString(),
      text: chip.getPrompt(userProfile),
      sender: 'user',
      timestamp: new Date(),
    };
    addMessage(userMessage);

    // Add follow-up question based on the selected chip
    let followUpQuestion = '';
    switch (chip.id) {
      case '1': // I want to share what's on my mind
        followUpQuestion = "I'd love to hear more about what's on your mind. Could you share what's been occupying your thoughts lately?";
        break;
      case '2': // I want to talk about how I'm feeling
        followUpQuestion = "I'm here to listen. Could you tell me more about your current emotional state and what might be affecting it?";
        break;
      case '3': // I'm facing some challenges
        followUpQuestion = "I'm here to help. Could you describe the challenges you're facing and how they're affecting you?";
        break;
      case '4': // I'm looking for guidance
        followUpQuestion = "I'd be happy to provide guidance. What specific area of your life would you like to explore or get insights about?";
        break;
    }

    // Add the follow-up question as an assistant message
    addMessage({
      id: (Date.now() + 1).toString(),
      text: followUpQuestion,
      sender: 'assistant',
      timestamp: new Date(),
    });

    // Update chat history
    setChatHistory(prev => [
      ...prev,
      { role: 'user', content: chip.text },
      { role: 'assistant', content: followUpQuestion }
    ]);
  };

  // Main send handler
  const handleSend = async () => {
    if (!inputText.trim()) return;
    // If userProfile is missing, treat first input as name
    if (!userProfile || !userProfile.name) {
      updateUserProfile('name', inputText.trim());
      addMessage({
        id: Date.now().toString(),
        text: inputText.trim(),
        sender: 'user',
        timestamp: new Date(),
      });
      addMessage({
        id: (Date.now() + 1).toString(),
        text: getFieldPrompt('birthDate'),
        sender: 'assistant',
        timestamp: new Date(),
      });
      setInputText('');
      // Focus input after sending
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return;
    }
    // If any birth details are missing, handle them in order
    const missingField = REQUIRED_FIELDS.find(field => !(userProfile as any)[field]);
    if (missingField) {
      let valid = true;
      if (missingField === 'birthDate') valid = isValidDate(inputText.trim());
      if (missingField === 'birthTime') valid = isValidTime(inputText.trim());
      if (missingField === 'birthPlace') valid = isValidLocation(inputText.trim());
      if (!valid) {
        addMessage({
          id: Date.now().toString(),
          text: `Please enter a valid ${missingField === 'birthDate' ? 'birth date (MM/DD/YYYY)' : missingField === 'birthTime' ? 'birth time (HH:MM, 24-hour format)' : 'birth place as "City, Country"'}.`,
          sender: 'assistant',
          timestamp: new Date(),
        });
        return;
      }
      // Save to AsyncStorage and Firestore
      updateUserProfile(missingField as keyof UserProfile, inputText.trim());
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (user) {
          await updateDoc(doc(db, 'users', user.uid), {
            [missingField]: inputText.trim(),
          });
        }
        // Update local userProfile state after saving
        setUserProfile(prev => prev ? { ...prev, [missingField]: inputText.trim() } : prev);
      } catch (e) {
        console.error('Error updating Firestore profile:', e);
      }
      addMessage({
        id: Date.now().toString(),
        text: inputText.trim(),
        sender: 'user',
        timestamp: new Date(),
      });
      // Ask for next missing field or continue chat
      const nextMissing = REQUIRED_FIELDS.find(field => !(userProfile as any)[field] && field !== missingField);
      if (nextMissing) {
        // Prevent duplicate prompts for missing fields
        const lastAssistantMessage = messages.find(m => m.sender === 'assistant');
        if (!lastAssistantMessage || lastAssistantMessage.text !== getFieldPrompt(nextMissing)) {
          addMessage({
            id: (Date.now() + 1).toString(),
            text: getFieldPrompt(nextMissing),
            sender: 'assistant',
            timestamp: new Date(),
          });
        }
      } else {
        addMessage({
          id: (Date.now() + 2).toString(),
          text: "Thank you! I've saved your information. I'd love to help you explore your astrological journey. What would you like to know?",
          sender: 'assistant',
          timestamp: new Date(),
        });
        setShowPromptChips(true);
      }
      setInputText('');
      // Focus input after sending
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return;
    }
    // General chat: send to OpenAI LLM
    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputText,
      sender: 'user',
      timestamp: new Date(),
    };
    addMessage(userMessage);
    setChatHistory(prev => [...prev, { role: 'user', content: inputText }]);
    setInputText('');
    // Focus input after sending
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    setIsLoading(true);
    startTypingAnimation(); // Start animation when loading begins

    try {
      // Use last 10 messages for context
      const contextMessages = chatHistory.slice(-9).concat({ role: 'user', content: inputText });
      const response = await AstrologyService.getAstrologyInsight(userMessage.text, userProfile, contextMessages);
      addMessage({
        id: (Date.now() + 1).toString(),
        text: response,
        sender: 'assistant' as 'assistant',
        timestamp: new Date(),
      });
      setChatHistory(prev => [...prev, { role: 'assistant', content: response }]);
    } catch (error) {
      addMessage({
        id: (Date.now() + 2).toString(),
        text: "I'm sorry, I encountered an error. Please try again.",
        sender: 'assistant' as 'assistant',
        timestamp: new Date(),
      });
      setChatHistory(prev => [...prev, { role: 'assistant', content: "I'm sorry, I encountered an error. Please try again." }]);
    } finally {
      setIsLoading(false);
      stopTypingAnimation(); // Stop animation when loading ends
    }
  };

  const handleScroll = (event: any) => {
    const offsetY = event.nativeEvent.contentOffset.y;
    if (offsetY > 50) {
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start(() => setShowFAB(false));
    } else {
      setShowFAB(true);
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  };

  const openMenu = () => setMenuVisible(true);
  const closeMenu = () => setMenuVisible(false);

  const handleLogout = async () => {
    closeMenu();
    try {
      await AuthenticationService.logout();
      // The navigation will be handled by the auth state change in App.tsx
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <KeyboardAvoidingView 
      style={[styles.container, { backgroundColor: colors.background }]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
    >
      <View style={[styles.header, { backgroundColor: '#111', borderBottomWidth: 1, borderBottomColor: '#232323', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }]}>
        <View style={[styles.headerContent, { flex: 1 }]}>
          <Text style={styles.title}>{'Astra'}</Text>
          <Text style={styles.subtitle}>Your Personal Astrology Assistant</Text>
        </View>
        <Menu
          visible={menuVisible}
          onDismiss={closeMenu}
          anchor={
            <TouchableOpacity onPress={openMenu} style={{ padding: 8 }}>
              <Ionicons name="ellipsis-vertical" size={28} color="#fff" />
            </TouchableOpacity>
          }
        >
          <Menu.Item onPress={handleLogout} title="Logout" leadingIcon="logout" />
        </Menu>
      </View>

      {chatLoading ? (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ActivityIndicator size="large" color="#fff" />
          <Text style={{ color: '#fff', marginTop: 16 }}>Loading your chat history...</Text>
        </View>
      ) : (
        <FlatList
          ref={scrollViewRef}
          data={messages}
          keyExtractor={(item) => item.id}
          renderItem={({ item: message }) => (
            <View
              style={[
                styles.messageContainer,
                message.sender === 'user' ? styles.userMessage : styles.assistantMessage
              ]}
            >
              {message.sender === 'assistant' && (
                <Avatar.Icon
                  size={32}
                  icon="star-outline"
                  style={[styles.avatar, { backgroundColor: '#181818', borderWidth: 1, borderColor: '#232323' }]}
                  color="#fff"
                />
              )}
              <View style={[
                styles.messageBubble,
                message.sender === 'user' ? styles.userBubble : styles.assistantBubble
              ]}>
                {message.sender === 'assistant' ? (
                  <Markdown style={markdownStyles}>{message.text}</Markdown>
                ) : (
                  <Text style={[styles.messageText, styles.userText]}>{message.text}</Text>
                )}
                <Text style={styles.timestamp}>
                  {format(message.timestamp, 'HH:mm')}
                </Text>
              </View>
            </View>
          )}
          contentContainerStyle={styles.messagesContent}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          inverted={true}
          ListHeaderComponent={
            loadingMore ? (
              <View style={{ alignItems: 'center', marginBottom: 12 }}>
                <ActivityIndicator size="small" color="#fff" />
                <Text style={{ color: '#fff', marginTop: 4 }}>Loading more...</Text>
              </View>
            ) : null
          }
          ListFooterComponent={
            <>
              {isLoading && (
                <View style={styles.typingIndicator}>
                  <Avatar.Icon
                    size={32}
                    icon="star-outline"
                    style={[styles.avatar, { backgroundColor: '#181818', borderWidth: 1, borderColor: '#232323' }]}
                    color="#fff"
                  />
                  <View style={styles.typingBubble}>
                    <Animated.View 
                      style={[
                        styles.typingDot,
                        {
                          opacity: dot1Anim,
                          transform: [{
                            translateY: dot1Anim.interpolate({
                              inputRange: [0, 1],
                              outputRange: [0, -4]
                            })
                          }]
                        }
                      ]} 
                    />
                    <Animated.View 
                      style={[
                        styles.typingDot,
                        {
                          opacity: dot2Anim,
                          transform: [{
                            translateY: dot2Anim.interpolate({
                              inputRange: [0, 1],
                              outputRange: [0, -4]
                            })
                          }]
                        }
                      ]} 
                    />
                    <Animated.View 
                      style={[
                        styles.typingDot,
                        {
                          opacity: dot3Anim,
                          transform: [{
                            translateY: dot3Anim.interpolate({
                              inputRange: [0, 1],
                              outputRange: [0, -4]
                            })
                          }]
                        }
                      ]} 
                    />
                  </View>
                </View>
              )}
            </>
          }
          onEndReachedThreshold={0.5}
          onEndReached={async () => {
            // Only load more if not already loading and there are messages
            if (!loadingMore && messages.length > 0) {
              const auth = getAuth();
              const user = auth.currentUser;
              if (user) {
                await loadMoreMessages(user.uid);
              }
            }
          }}
        />
      )}

      <View style={styles.inputContainer}>
        {/* Typing animation always visible above the input */}
        {isLoading && (
          <View style={styles.typingIndicator}>
            <Avatar.Icon
              size={32}
              icon="star-outline"
              style={[styles.avatar, { backgroundColor: '#181818', borderWidth: 1, borderColor: '#232323' }]}
              color="#fff"
            />
            <View style={styles.typingBubble}>
              <Animated.View 
                style={[
                  styles.typingDot,
                  {
                    opacity: dot1Anim,
                    transform: [{
                      translateY: dot1Anim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [0, -4]
                      })
                    }]
                  }
                ]} 
              />
              <Animated.View 
                style={[
                  styles.typingDot,
                  {
                    opacity: dot2Anim,
                    transform: [{
                      translateY: dot2Anim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [0, -4]
                      })
                    }]
                  }
                ]} 
              />
              <Animated.View 
                style={[
                  styles.typingDot,
                  {
                    opacity: dot3Anim,
                    transform: [{
                      translateY: dot3Anim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [0, -4]
                      })
                    }]
                  }
                ]} 
              />
            </View>
          </View>
        )}
        {/* Chips always visible above the input */}
        {showPromptChips && (
          <View style={styles.promptChipsContainer}>
            {PROMPT_CHIPS.map((chip) => (
              <Chip
                key={chip.id}
                icon={chip.icon}
                onPress={() => handlePromptChipPress(chip)}
                style={styles.promptChip}
                textStyle={styles.promptChipText}
                mode="outlined"
              >
                {chip.text}
              </Chip>
            ))}
          </View>
        )}
        <TextInput
          ref={inputRef}
          mode="outlined"
          value={inputText}
          onChangeText={setInputText}
          placeholder="Your message"
          style={styles.input}
          right={
            <TextInput.Icon
              icon="send"
              onPress={handleSend}
              disabled={!inputText.trim() || isLoading}
            />
          }
          disabled={isLoading}
          onSubmitEditing={() => {
            if (inputText.trim() && !isLoading) handleSend();
          }}
          returnKeyType="send"
          autoFocus={true}
        />
      </View>

      <Portal>
        <Animated.View style={[styles.fabContainer, { opacity: fadeAnim }]}>
          {/*
          <FAB.Group
            visible={showFAB}
            open={false}
            icon="plus"
            actions={[
              {
                icon: 'chart-bar',
                label: 'Birth Chart',
                onPress: () => handleAstrologyQuery('Show me my birth chart'),
              },
              {
                icon: 'calendar',
                label: 'Daily Horoscope',
                onPress: () => handleAstrologyQuery('What is my daily horoscope?'),
              },
              {
                icon: 'heart',
                label: 'Compatibility',
                onPress: () => handleAstrologyQuery('Tell me about my astrological compatibility'),
              },
            ]}
            onStateChange={() => {}}
          />
          */}
        </Animated.View>
      </Portal>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#111',
  },
  header: {
    paddingTop: 50,
    paddingBottom: 20,
    paddingHorizontal: 20,
    backgroundColor: '#111',
    borderBottomWidth: 1,
    borderBottomColor: '#232323',
  },
  headerContent: {
    alignItems: 'center',
  },
  title: {
    fontSize: 36,
    fontFamily: 'Georgia',
    color: '#fff',
    marginBottom: 4,
    fontWeight: '700',
  },
  subtitle: {
    fontSize: 18,
    color: '#B0B0B0',
    opacity: 1,
    fontFamily: 'System',
  },
  messagesContainer: {
    flex: 1,
    backgroundColor: '#111',
  },
  messagesContent: {
    padding: 24,
  },
  welcomeContainer: {
    alignItems: 'center',
    padding: 32,
    marginTop: 40,
  },
  welcomeIcon: {
    marginBottom: 24,
    backgroundColor: '#181818',
    borderWidth: 1,
    borderColor: '#232323',
  },
  welcomeTitle: {
    fontSize: 28,
    fontFamily: 'Georgia',
    fontWeight: '700',
    color: '#fff',
    marginBottom: 12,
    textAlign: 'center',
  },
  welcomeText: {
    fontSize: 16,
    color: '#B0B0B0',
    textAlign: 'center',
    marginBottom: 24,
    fontFamily: 'System',
  },
  startButton: {
    width: '80%',
    backgroundColor: '#181818',
    borderWidth: 1,
    borderColor: '#232323',
    color: '#fff',
  },
  messageContainer: {
    flexDirection: 'row',
    marginBottom: 24,
    alignItems: 'flex-end',
  },
  userMessage: {
    justifyContent: 'flex-end',
  },
  assistantMessage: {
    justifyContent: 'flex-start',
  },
  avatar: {
    marginRight: 8,
    backgroundColor: '#181818',
    borderWidth: 1,
    borderColor: '#232323',
  },
  messageBubble: {
    maxWidth: '80%',
    padding: 18,
    borderRadius: 18,
    marginBottom: 4,
    borderWidth: 1,
    borderColor: '#232323',
    backgroundColor: '#181818',
  },
  userBubble: {
    backgroundColor: '#181818',
    borderColor: '#232323',
  },
  assistantBubble: {
    backgroundColor: '#181818',
    borderColor: '#232323',
  },
  messageText: {
    fontSize: 18,
    lineHeight: 28,
    color: '#fff',
    fontFamily: 'System',
  },
  userText: {
    color: '#fff',
  },
  assistantText: {
    color: '#fff',
  },
  timestamp: {
    fontSize: 13,
    color: '#B0B0B0',
    marginTop: 8,
    alignSelf: 'flex-end',
    fontFamily: 'System',
  },
  inputContainer: {
    padding: 24,
    borderTopWidth: 1,
    borderTopColor: '#232323',
    backgroundColor: '#181818',
  },
  input: {
    backgroundColor: '#181818',
    color: '#fff',
    borderWidth: 1,
    borderColor: '#232323',
    fontFamily: 'System',
  },
  typingIndicator: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginBottom: 24,
    paddingLeft: 8,
  },
  typingBubble: {
    flexDirection: 'row',
    backgroundColor: '#181818',
    padding: 12,
    borderRadius: 18,
    marginLeft: 8,
    borderWidth: 1,
    borderColor: '#232323',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  typingDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#fff',
    marginHorizontal: 2,
  },
  fabContainer: {
    position: 'absolute',
    right: 16,
    bottom: 80,
  },
  gradientBg: {
    flex: 1,
    backgroundColor: '#111',
  },
  onboardingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
    paddingTop: 60,
    backgroundColor: '#111',
  },
  onboardingTitle: {
    fontSize: 48,
    fontWeight: '700',
    color: '#fff',
    marginBottom: 8,
    fontFamily: 'Georgia',
    letterSpacing: 1,
  },
  onboardingSubtitle: {
    fontSize: 20,
    color: '#B0B0B0',
    marginBottom: 40,
    fontWeight: '600',
    fontFamily: 'System',
  },
  onboardingBubble: {
    backgroundColor: '#181818',
    borderRadius: 32,
    padding: 28,
    marginBottom: 40,
    width: '100%',
    maxWidth: 420,
    borderWidth: 1,
    borderColor: '#232323',
    flexDirection: 'row',
    alignItems: 'center',
  },
  onboardingIconWrap: {
    backgroundColor: '#181818',
    borderRadius: 24,
    width: 48,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 18,
    borderWidth: 1,
    borderColor: '#232323',
  },
  onboardingBubbleText: {
    color: '#fff',
    fontSize: 20,
    fontWeight: '500',
    flex: 1,
    fontFamily: 'System',
  },
  onboardingInputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    maxWidth: 420,
    backgroundColor: '#181818',
    borderRadius: 32,
    paddingHorizontal: 24,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#232323',
  },
  onboardingInput: {
    flex: 1,
    fontSize: 24,
    color: '#fff',
    backgroundColor: 'transparent',
    fontWeight: '600',
    fontFamily: 'System',
    paddingVertical: 8,
  },
  onboardingActionBtn: {
    marginLeft: 12,
    borderRadius: 32,
    overflow: 'hidden',
  },
  onboardingActionGradient: {
    width: 56,
    height: 56,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#181818',
    borderWidth: 1,
    borderColor: '#232323',
  },
  promptChipsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 16,
    gap: 8,
    backgroundColor: '#181818',
    borderTopWidth: 1,
    borderTopColor: '#232323',
  },
  promptChip: {
    backgroundColor: '#232323',
    borderColor: '#333',
    borderWidth: 1,
    marginRight: 8,
    marginBottom: 8,
  },
  promptChipText: {
    color: '#fff',
    fontSize: 14,
    fontFamily: 'System',
  },
});

// Markdown styles with proper type definitions
const markdownStyles = {
  body: { color: '#fff', fontSize: 18, fontFamily: 'System' },
  heading1: { color: '#fff', fontSize: 28, fontFamily: 'Georgia', fontWeight: '700' as const, marginBottom: 8 },
  heading2: { color: '#fff', fontSize: 22, fontFamily: 'Georgia', fontWeight: '700' as const, marginBottom: 6 },
  heading3: { color: '#fff', fontSize: 18, fontFamily: 'Georgia', fontWeight: '700' as const, marginBottom: 4 },
  strong: { fontWeight: '700' as const, color: '#fff' },
  bullet_list: { marginVertical: 4 },
  ordered_list: { marginVertical: 4 },
  list_item: { flexDirection: 'row' as const, color: '#fff' },
  paragraph: { marginBottom: 8, color: '#fff' },
  link: { color: '#fff', textDecorationLine: 'underline' as const },
  code_inline: { backgroundColor: '#232323', color: '#fff', borderRadius: 4, padding: 2 },
}; 
